import React from 'react';
import { motion } from 'framer-motion';
export interface ProtractorProps {
  /** The angle to display in degrees (0-360) */
  angle: number;
  /** Optional second angle for comparison */
  compareAngle?: number;
  /** Show the angle value on the protractor */
  showValue?: boolean;
  /** Type of angle to highlight */
  angleType?: 'scherp' | 'recht' | 'stomp' | 'gestrekt' | 'overig';
  /** Optional label */
  label?: string;
  /** 'stimulus' = compact, 'support' = educational panel */
  mode?: 'stimulus' | 'support';
}
function classifyAngle(deg: number): string {
  if (deg === 90) return 'recht';
  if (deg === 180) return 'gestrekt';
  if (deg > 0 && deg < 90) return 'scherp';
  if (deg > 90 && deg < 180) return 'stomp';
  return 'overig';
}
function angleName(type: string): string {
  const names: Record<string, string> = {
    scherp: 'Scherpe hoek',
    recht: 'Rechte hoek',
    stomp: 'Stompe hoek',
    gestrekt: 'Gestrekte hoek',
    overig: 'Hoek'
  };
  return names[type] ?? 'Hoek';
}
function angleRange(type: string): string {
  const ranges: Record<string, string> = {
    scherp: '0° < hoek < 90°',
    recht: 'hoek = 90°',
    stomp: '90° < hoek < 180°',
    gestrekt: 'hoek = 180°',
    overig: ''
  };
  return ranges[type] ?? '';
}
const ANGLE_COLORS: Record<string, {
  fill: string;
  stroke: string;
  text: string;
  bg: string;
}> = {
  scherp: {
    fill: '#dbeafe',
    stroke: '#3b82f6',
    text: 'text-blue-700',
    bg: 'bg-blue-50'
  },
  recht: {
    fill: '#dcfce7',
    stroke: '#22c55e',
    text: 'text-green-700',
    bg: 'bg-green-50'
  },
  stomp: {
    fill: '#fef3c7',
    stroke: '#f59e0b',
    text: 'text-amber-700',
    bg: 'bg-amber-50'
  },
  gestrekt: {
    fill: '#fce7f3',
    stroke: '#ec4899',
    text: 'text-pink-700',
    bg: 'bg-pink-50'
  },
  overig: {
    fill: '#f1f5f9',
    stroke: '#64748b',
    text: 'text-slate-700',
    bg: 'bg-slate-50'
  }
};
function ProtractorSVG({
  angle,
  showValue = true,
  size = 200,
  colors
}: {
  angle: number;
  showValue?: boolean;
  size?: number;
  colors: {
    fill: string;
    stroke: string;
  };
}) {
  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.4;
  const innerRadius = radius * 0.3;
  // Convert angle to radians (measuring from 3 o'clock, counterclockwise)
  const angleRad = angle * Math.PI / 180;
  // End point of the angle arm
  const armX = cx + radius * Math.cos(-angleRad);
  const armY = cy + radius * Math.sin(-angleRad);
  // Arc path for the angle
  const largeArc = angle > 180 ? 1 : 0;
  const arcEndX = cx + innerRadius * Math.cos(-angleRad);
  const arcEndY = cy + innerRadius * Math.sin(-angleRad);
  // Tick marks every 10 degrees
  const ticks = Array.from({
    length: 19
  }, (_, i) => i * 10);
  return <svg viewBox={`0 0 ${size} ${size * 0.6}`} width="100%" style={{
    maxWidth: size
  }} aria-label={`Gradenboog met hoek van ${angle}°`} data-id="element-9862">
      {/* Semi-circle outline */}
      <path d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`} fill="none" stroke="#cbd5e1" strokeWidth="1.5" data-id="element-9863" />

      {/* Base line */}
      <line x1={cx - radius - 5} y1={cy} x2={cx + radius + 5} y2={cy} stroke="#94a3b8" strokeWidth="1.5" data-id="element-9864" />

      {/* Tick marks */}
      {ticks.map(deg => {
      const rad = deg * Math.PI / 180;
      const outerX = cx + radius * Math.cos(-rad);
      const outerY = cy + radius * Math.sin(-rad);
      const innerX = cx + (radius - (deg % 30 === 0 ? 12 : 6)) * Math.cos(-rad);
      const innerY = cy + (radius - (deg % 30 === 0 ? 12 : 6)) * Math.sin(-rad);
      return <g key={deg} data-id="element-9865">
            <line x1={innerX} y1={innerY} x2={outerX} y2={outerY} stroke="#94a3b8" strokeWidth={deg % 30 === 0 ? 1.5 : 0.75} data-id="element-9866" />
            {deg % 30 === 0 && <text x={cx + (radius + 14) * Math.cos(-rad)} y={cy + (radius + 14) * Math.sin(-rad)} textAnchor="middle" dominantBaseline="central" fontSize="9" fill="#64748b" fontWeight="500" data-id="element-9867">
                {deg}°
              </text>}
          </g>;
    })}

      {/* Angle arc fill */}
      <motion.path d={`M ${cx + innerRadius} ${cy} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${arcEndX} ${arcEndY} L ${cx} ${cy} Z`} fill={colors.fill} fillOpacity={0.6} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.3,
      duration: 0.4
    }} data-id="element-9868" />

      {/* Angle arc stroke */}
      <motion.path d={`M ${cx + innerRadius} ${cy} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${arcEndX} ${arcEndY}`} fill="none" stroke={colors.stroke} strokeWidth="2" initial={{
      pathLength: 0
    }} animate={{
      pathLength: 1
    }} transition={{
      delay: 0.2,
      duration: 0.5,
      ease: 'easeOut'
    }} data-id="element-9869" />

      {/* Angle arm */}
      <motion.line x1={cx} y1={cy} x2={armX} y2={armY} stroke={colors.stroke} strokeWidth="2" strokeLinecap="round" initial={{
      pathLength: 0
    }} animate={{
      pathLength: 1
    }} transition={{
      delay: 0.1,
      duration: 0.4
    }} data-id="element-9870" />

      {/* Base arm */}
      <line x1={cx} y1={cy} x2={cx + radius} y2={cy} stroke={colors.stroke} strokeWidth="2" strokeLinecap="round" data-id="element-9871" />

      {/* Center dot */}
      <circle cx={cx} cy={cy} r="3" fill={colors.stroke} data-id="element-9872" />

      {/* Angle value */}
      {showValue && <motion.text x={cx + (innerRadius + 16) * Math.cos(-angleRad / 2)} y={cy + (innerRadius + 16) * Math.sin(-angleRad / 2)} textAnchor="middle" dominantBaseline="central" fontSize="13" fontWeight="700" fill={colors.stroke} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.5
    }} data-id="element-9873">
          {angle}°
        </motion.text>}

      {/* Right angle marker */}
      {angle === 90 && <rect x={cx + 4} y={cy - 12} width="8" height="8" fill="none" stroke={colors.stroke} strokeWidth="1.5" data-id="element-9874" />}
    </svg>;
}
export function Protractor({
  angle,
  compareAngle,
  showValue = true,
  angleType,
  label,
  mode = 'support'
}: ProtractorProps) {
  const clampedAngle = Math.max(0, Math.min(360, angle));
  const type = angleType ?? classifyAngle(clampedAngle);
  const colors = ANGLE_COLORS[type] ?? ANGLE_COLORS.overig;
  // Stimulus mode: compact
  if (mode === 'stimulus') {
    return <div className={`${colors.bg} rounded-xl px-5 py-4 border border-slate-200`} aria-label={label ?? `Hoek van ${clampedAngle}°`} data-id="element-9875">
        {label && <p className={`text-sm font-semibold ${colors.text} mb-2 text-center`} data-id="element-9876">
            {label}
          </p>}
        <ProtractorSVG angle={clampedAngle} showValue={showValue} size={180} colors={colors} data-id="element-9877" />
      </div>;
  }
  // Support mode: educational panel
  return <div className="bg-gradient-to-br from-slate-50 to-blue-50 p-6 rounded-2xl border-2 border-slate-200 shadow-sm" aria-label={label ?? `Gradenboog: ${clampedAngle}°`} data-id="element-9878">
      {label && <div className="mb-5 text-center" data-id="element-9879">
          <div className="inline-flex items-center gap-2 bg-white/90 rounded-xl px-4 py-2 border border-slate-200 shadow-sm" data-id="element-9880">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" data-id="element-9881" />
            <span className="text-sm font-bold text-slate-900" data-id="element-9882">{label}</span>
          </div>
        </div>}

      {/* Protractor visualization */}
      <div className="bg-white/80 rounded-xl p-4 border border-slate-100 mb-4 flex justify-center" data-id="element-9883">
        <ProtractorSVG angle={clampedAngle} showValue={showValue} size={220} colors={colors} data-id="element-9884" />
      </div>

      {/* Angle classification */}
      <div className="bg-white/80 rounded-xl p-4 border border-slate-100 mb-4" data-id="element-9885">
        <div className="flex items-center justify-between" data-id="element-9886">
          <div data-id="element-9887">
            <p className="text-xs text-slate-500 font-medium" data-id="element-9888">Type hoek</p>
            <p className={`text-lg font-bold ${colors.text}`} data-id="element-9889">
              {angleName(type)}
            </p>
          </div>
          <div className="text-right" data-id="element-9890">
            <p className="text-xs text-slate-500 font-medium" data-id="element-9891">Grootte</p>
            <p className={`text-lg font-bold ${colors.text}`} data-id="element-9892">
              {clampedAngle}°
            </p>
          </div>
        </div>
        {angleRange(type) && <p className="text-xs text-slate-500 mt-2 text-center font-mono" data-id="element-9893">
            {angleRange(type)}
          </p>}
      </div>

      {/* Compare angle */}
      {compareAngle !== undefined && <div className="bg-white/80 rounded-xl p-4 border border-slate-100 mb-4" data-id="element-9894">
          <p className="text-xs text-slate-500 font-medium mb-2" data-id="element-9895">
            Vergelijking
          </p>
          <div className="flex items-center gap-4 justify-center" data-id="element-9896">
            <div className={`px-3 py-1.5 rounded-lg ${colors.bg} ${colors.text} font-bold`} data-id="element-9897">
              {clampedAngle}°
            </div>
            <span className="text-slate-400" data-id="element-9898">vs</span>
            <div className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 font-bold" data-id="element-9899">
              {compareAngle}°
            </div>
          </div>
        </div>}

      {/* Educational explanation */}
      <div className="bg-white/80 rounded-xl p-4 border border-slate-100 shadow-sm" data-id="element-9900">
        <div className="flex items-start gap-3" data-id="element-9901">
          <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-9902">
            <span className="text-blue-600 text-sm" data-id="element-9903">📐</span>
          </div>
          <div data-id="element-9904">
            <h4 className="text-sm font-bold text-slate-900 mb-1" data-id="element-9905">
              Soorten hoeken
            </h4>
            <div className="grid grid-cols-2 gap-1.5 text-xs text-slate-600" data-id="element-9906">
              <div className={type === 'scherp' ? 'font-bold text-blue-700' : ''} data-id="element-9907">
                Scherp: 0° – 90°
              </div>
              <div className={type === 'recht' ? 'font-bold text-green-700' : ''} data-id="element-9908">
                Recht: precies 90°
              </div>
              <div className={type === 'stomp' ? 'font-bold text-amber-700' : ''} data-id="element-9909">
                Stomp: 90° – 180°
              </div>
              <div className={type === 'gestrekt' ? 'font-bold text-pink-700' : ''} data-id="element-9910">
                Gestrekt: precies 180°
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}